import { types, flow, Instance, getRoot } from 'mobx-state-tree';
import { isObject } from 'lodash';

import { RouterState } from 'connected-react-router';
import { EmailTemplatesAPI as API } from '@src/services/companies';
import { getCompanyDomain } from '@src/helpers';
import * as toast from 'src/components/Toast/index';
import { ApiStore } from '@src/features/common';

const EmailTemplate = types.model('EmailTemplate', {
  id: types.maybeNull(types.number),
  mail_from: types.string,
  name: types.string,
  subject: types.string,
  template: types.string,
  type: types.maybeNull(
    types.enumeration('Type', [
      'interest_from_upload',
      'custom',
      'apply_from_upload',
      'interest_from_apply',
      'partner_invite',
      'transfer',
      'apply',
      'reject'
    ])
  )
});

const initialTemplate = {
  id: null,
  mail_from: '',
  name: '',
  subject: '',
  template: '',
  type: 'custom'
} as Instance<typeof EmailTemplate>;

export const EmailTemplatesStore = types
  .compose(
    ApiStore,
    types.model('EmailTemplatesModel', {
      isSuccess: types.optional(types.maybeNull(types.boolean), null),
      templates: types.optional(types.array(EmailTemplate), []),
      currentTemplate: types.optional(EmailTemplate, initialTemplate)
    })
  )
  .actions(self => {
    return {
      updateCurrentTemplate: arg => {
        if (Array.isArray(arg)) {
          const [field, value] = arg;
          self.currentTemplate[field] = value;
          return;
        }
        if (isObject(arg as IEmailTemplate)) {
          self.currentTemplate = arg;
        }
      },
      clearCurrentTemplate: () => {
        self.currentTemplate = initialTemplate;
      },

      fetchTemplates: flow(function* fetch(qs = '') {
        const res = yield self.fetch(API.getCompanyEmailTemplates(qs));
        self.templates = res;
      }),
      fetchTemplate: flow(function* fetch(templateId) {
        const res = yield self.fetch(API.getCompanyEmailTemplate(templateId));
        self.currentTemplate = res;
      })
    };
  })
  .actions(self => {
    const companyId = getCompanyDomain();
    const rootStore = getRoot<{ routerStore: RouterState }>(self);

    return {
      fetchData: flow(function* fetch() {
        self.isSuccess = false;

        try {
          yield self.fetchTemplates(rootStore.routerStore.location.search);
          self.isSuccess = true;
        } catch (error) {}
      }),
      deleteTemplate: flow(function* fetch(templateId) {
        yield self.delete(API.deleteCompanyEmailTemplate(templateId));
        yield self.fetchTemplates();
        toast.info('Template has been deleted');
      }),
      updateTemplate: flow(function* fetch() {
        try {
          const templateConfig = self.currentTemplate;
          yield self.patch(API.updateCompanyEmailTemplate(templateConfig.id), {
            ...templateConfig,
            company_id: companyId
          });

          toast.info('Template has been updated');

          rootStore.routerStore.push('/preferences/email_templates');
        } catch (error) {
          toast.info('Template update error occured');
        }

        self.fetchTemplates();
      }),
      createTemplate: flow(function* fetch() {
        try {
          const templateConfig = self.currentTemplate;
          yield self.post(API.createCompanyEmailTemplate(), {
            ...templateConfig,
            company_id: companyId
          });

          toast.info('Template has been created');
          rootStore.routerStore.push('/preferences/email_templates');
        } catch (error) {
          toast.error('Template create error occured');
        }

        self.fetchTemplates();
      })
    };
  });

export type IEmailTemplate = Instance<typeof EmailTemplate>;
